import { useState, useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './SalesReport.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const todayISO = () => new Date().toISOString().slice(0, 10);
const startOfMonthISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const fmt    = (n) => n == null ? '—' : Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (n) => n == null ? '—' : Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });

/* ── Multi-select dropdown for Sales Channels ─────────────── */
const ChannelMultiSelect = ({ channels, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (id) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  const selectAll  = () => onChange(channels.map((c) => String(c.id)));
  const clearAll   = () => onChange([]);

  const label = selected.length === 0
    ? 'All Channels'
    : selected.length === channels.length
    ? 'All Channels'
    : `${selected.length} channel${selected.length > 1 ? 's' : ''} selected`;

  return (
    <div className="sr-ms" ref={ref}>
      <button type="button" className={`sr-ms-handle${open ? ' open' : ''}`} onClick={() => setOpen((o) => !o)}>
        <span className="sr-ms-label">{label}</span>
        <span className="sr-ms-arrow">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="sr-ms-dropdown">
          <div className="sr-ms-actions">
            <button type="button" onClick={selectAll}>Select All</button>
            <button type="button" onClick={clearAll}>Clear</button>
          </div>
          <div className="sr-ms-list">
            {channels.map((ch) => (
              <label key={ch.id} className="sr-ms-item">
                <input
                  type="checkbox"
                  checked={selected.includes(String(ch.id))}
                  onChange={() => toggle(String(ch.id))}
                />
                <span>{ch.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Main page ────────────────────────────────────────────── */
const SalesReport = () => {
  const { user } = useContext(AuthContext);
  const authHdr = { Authorization: `Bearer ${user?.token}` };

  const [fromDate,    setFromDate]    = useState(startOfMonthISO());
  const [toDate,      setToDate]      = useState(todayISO());
  const [categoryId,  setCategoryId]  = useState('');
  const [subcatId,    setSubcatId]    = useState('');
  const [productId,   setProductId]   = useState('');
  const [channelIds,  setChannelIds]  = useState([]);

  const [categories,    setCategories]    = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [products,      setProducts]      = useState([]);
  const [channels,      setChannels]      = useState([]);

  const [rows,    setRows]    = useState([]);
  const [ran,     setRan]     = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  // Load categories + channels once
  useEffect(() => {
    axios.get(`${API_URL}/categories`, { headers: authHdr, params: { type: 'product' } })
      .then((r) => setCategories(r.data)).catch(() => {});
    axios.get(`${API_URL}/commonLOVs/saleschannels`, { headers: authHdr })
      .then((r) => setChannels(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  // Load products for given category + optional subcategory
  const loadProducts = (catId, subId) => {
    if (!catId) { setProducts([]); return; }
    const params = { status: 'A', category_id: catId };
    if (subId) params.subcategory_id = subId;
    axios.get(`${API_URL}/products/products`, { headers: authHdr, params })
      .then((r) => setProducts(r.data)).catch(() => {});
  };

  const handleCategoryChange = (catId) => {
    setCategoryId(catId);
    setSubcatId('');
    setProductId('');
    setSubcategories([]);
    setProducts([]);
    if (!catId) return;
    axios.get(`${API_URL}/categories/${catId}/subs`, { headers: authHdr, params: { type: 'product' } })
      .then((r) => setSubcategories(r.data)).catch(() => {});
    loadProducts(catId, '');
  };

  const handleSubcatChange = (subId) => {
    setSubcatId(subId);
    setProductId('');
    loadProducts(categoryId, subId);
  };

  const handleReset = () => {
    setFromDate(startOfMonthISO());
    setToDate(todayISO());
    setCategoryId('');
    setSubcatId('');
    setProductId('');
    setChannelIds([]);
    setSubcategories([]);
    setProducts([]);
    setRows([]);
    setRan(false);
    setError('');
  };

  const handleRun = async () => {
    if (!fromDate || !toDate) { setError('Please set From and To dates.'); return; }
    setLoading(true);
    setError('');
    try {
      const params = { from_date: fromDate, to_date: toDate };
      if (categoryId)         params.category_id       = categoryId;
      if (subcatId)           params.subcategory_id    = subcatId;
      if (productId)          params.product_id        = productId;
      if (channelIds.length)  params.sales_channel_ids = channelIds.join(',');
      const res = await axios.get(`${API_URL}/reports/sales-by-product`, { headers: authHdr, params });
      setRows(res.data);
      setRan(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load report.');
    } finally {
      setLoading(false);
    }
  };

  const totals = rows.reduce(
    (acc, r) => ({
      qty:  acc.qty  + Number(r.total_qty   || 0),
      sale: acc.sale + Number(r.sale_amount || 0),
      sgst: acc.sgst + Number(r.sgst_amount || 0),
      cgst: acc.cgst + Number(r.cgst_amount || 0),
      gst:  acc.gst  + Number(r.total_gst   || 0),
    }),
    { qty: 0, sale: 0, sgst: 0, cgst: 0, gst: 0 }
  );

  return (
    <div className="sr-page">
      <h2 className="sr-title">Sales Report — By Product</h2>

      <div className="sr-filters">
        <div className="sr-filter-group">
          <label className="sr-label">From Date</label>
          <input type="date" className="sr-input" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>

        <div className="sr-filter-group">
          <label className="sr-label">To Date</label>
          <input type="date" className="sr-input" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>

        <div className="sr-filter-group">
          <label className="sr-label">Category</label>
          <select className="sr-select" value={categoryId} onChange={(e) => handleCategoryChange(e.target.value)}>
            <option value="">All Categories</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div className="sr-filter-group">
          <label className="sr-label">Subcategory</label>
          <select className="sr-select" value={subcatId} onChange={(e) => handleSubcatChange(e.target.value)} disabled={!categoryId}>
            <option value="">All Subcategories</option>
            {subcategories.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <div className="sr-filter-group">
          <label className="sr-label">Product</label>
          <select className="sr-select" value={productId} onChange={(e) => setProductId(e.target.value)} disabled={!categoryId}>
            <option value="">All Products</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        <div className="sr-filter-group">
          <label className="sr-label">Sales Channel</label>
          <ChannelMultiSelect channels={channels} selected={channelIds} onChange={setChannelIds} />
        </div>
      </div>

      <div className="sr-run-row">
        <button className="sr-btn" onClick={handleRun} disabled={loading}>
          {loading ? 'Loading…' : 'Run Report'}
        </button>
        <button className="sr-btn sr-btn--reset" onClick={handleReset} disabled={loading}>
          Reset
        </button>
      </div>

      {error && <div className="sr-error">{error}</div>}

      {ran && !loading && !error && (
        <div className="sr-table-wrap">
          <table className="sr-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Subcategory</th>
                <th>Product</th>
                <th>HSN</th>
                <th className="sr-num">SGST %</th>
                <th className="sr-num">CGST %</th>
                <th className="sr-num">Qty</th>
                <th className="sr-num">Sale Amount</th>
                <th className="sr-num">SGST</th>
                <th className="sr-num">CGST</th>
                <th className="sr-num">Total GST</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={11} className="sr-empty">No data for selected filters.</td></tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.category_name    || '—'}</td>
                    <td>{r.subcategory_name || '—'}</td>
                    <td>{r.product_name}</td>
                    <td>{r.hsn_code || '—'}</td>
                    <td className="sr-num">{r.sgst_pct ?? '—'}</td>
                    <td className="sr-num">{r.cgst_pct ?? '—'}</td>
                    <td className="sr-num">{fmtQty(r.total_qty)}</td>
                    <td className="sr-num">{fmt(r.sale_amount)}</td>
                    <td className="sr-num">{fmt(r.sgst_amount)}</td>
                    <td className="sr-num">{fmt(r.cgst_amount)}</td>
                    <td className="sr-num">{fmt(r.total_gst)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="sr-total-row">
                  <td colSpan={6}><strong>Total</strong></td>
                  <td className="sr-num"><strong>{fmtQty(totals.qty)}</strong></td>
                  <td className="sr-num"><strong>{fmt(totals.sale)}</strong></td>
                  <td className="sr-num"><strong>{fmt(totals.sgst)}</strong></td>
                  <td className="sr-num"><strong>{fmt(totals.cgst)}</strong></td>
                  <td className="sr-num"><strong>{fmt(totals.gst)}</strong></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
};

export default SalesReport;
